import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Loads Razorpay Checkout script once and exposes a launchCheckout(courseId, amountInr) helper.
 * Requires the user to be authenticated. If not, redirects to /learning-hub.
 */
const useRazorpayCheckout = () => {
  const { user } = useAuth();
  const [loaded, setLoaded] = useState(!!window.Razorpay);

  useEffect(() => {
    if (window.Razorpay) { setLoaded(true); return; }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    s.onload = () => setLoaded(true);
    document.body.appendChild(s);
    return () => { try { document.body.removeChild(s); } catch (_) { /* ignore */ } };
  }, []);

  const launch = async ({ courseId, batchId, ebookId, bundleId, amountInr, courseName, onSuccess, onFailure }) => {
    if (!user) { toast.error('Please sign in to pay'); window.location.href = '/learning-hub'; return; }
    if (!loaded || !window.Razorpay) { toast.error('Payment SDK loading...'); return; }
    let order;
    try {
      const { data } = await api.post('/payments/create-order', {
        course_id: courseId || null,
        batch_id: batchId || null,
        ebook_id: ebookId || null,
        bundle_id: bundleId || null,
        amount: Math.round(amountInr * 100), // paise
      });
      order = data;
    } catch (err) { toast.error(formatApiError(err)); onFailure?.(err); return; }

    // Prefer server-side prefill (canonical profile from DB) over client `user` cache
    const pf = order.prefill || {};
    const options = {
      key: order.razorpay_key_id,
      amount: order.amount,
      currency: order.currency,
      name: 'BYMAT Automation',
      description: courseName || order.course_name || 'Course enrollment',
      order_id: order.order_id,
      prefill: {
        name: pf.name || user.full_name,
        email: pf.email || user.email,
        contact: pf.contact || user.mobile || '',
      },
      // Lock fields so students can't pay under a different identity
      readonly: { email: true, contact: true, name: true },
      theme: { color: '#00F0FF' },
      handler: async (resp) => {
        try {
          const { data: vData } = await api.post('/payments/verify', {
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
            payment_db_id: order.payment_db_id,
          });
          toast.success('Payment verified — access granted.');
          onSuccess?.(vData);
        } catch (err) {
          toast.error('Payment verification failed. Please retry payment.');
          onFailure?.(err);
        }
      },
      modal: {
        ondismiss: () => {
          toast.info('Payment cancelled');
          onFailure?.({ cancelled: true });
        },
      },
    };
    const rzp = new window.Razorpay(options);
    // Handle Razorpay's `payment.failed` event for proper Retry button states
    rzp.on('payment.failed', (err) => {
      toast.error('Payment failed. Please try again.');
      onFailure?.(err);
    });
    rzp.open();
  };

  return { ready: loaded, launch };
};

export default useRazorpayCheckout;
